import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { useReserve } from "../contexts/ReserveContext";
import { ASSETS, PHONE, PHONE_TEL } from "../data/constants";

const Navbar = () => {
    const { t, lang, toggle } = useLang();
    const { open: openReserve } = useReserve();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    const isHome = location.pathname === "/";
    const transparent = isHome && !scrolled && !open;

    const links = [
        { to: "/", label: t("nav.home") },
        { to: "/menu", label: t("nav.menu") },
        { to: "/events", label: t("nav.events") },
        { to: "/location", label: t("nav.location") },
    ];

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                transparent ? "bg-transparent" : "bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.04)]"
            }`}
        >
            <div className="container-x flex items-center justify-between h-20 md:h-24">
                {/* Left: language switch */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                        data-testid="language-toggle"
                        onClick={toggle}
                        className={`group flex items-center rounded-full border ${
                            transparent ? "border-white/40 text-white" : "border-aegean-700/20 text-aegean-800"
                        } overflow-hidden text-xs font-display tracking-[0.2em] uppercase shrink-0`}
                        aria-label="Toggle language"
                    >
                        <span
                            data-testid="lang-en"
                            className={`px-3 py-1.5 transition-colors ${
                                lang === "en"
                                    ? "bg-aegean-400 text-white"
                                    : transparent
                                    ? "text-white/80"
                                    : "text-aegean-700"
                            }`}
                        >
                            EN
                        </span>
                        <span
                            data-testid="lang-gr"
                            className={`px-3 py-1.5 transition-colors ${
                                lang === "gr"
                                    ? "bg-aegean-400 text-white"
                                    : transparent
                                    ? "text-white/80"
                                    : "text-aegean-700"
                            }`}
                        >
                            GR
                        </span>
                    </button>
                </div>

                {/* Center: logo */}
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="absolute left-1/2 -translate-x-1/2 flex items-center"
                >
                    <img
                        src={ASSETS.logoBlue}
                        alt="AMOS Beach Bar"
                        data-testid="navbar-logo"
                        className={`h-12 md:h-14 w-auto object-contain transition-[filter] duration-300 ${
                            transparent ? "brightness-0 invert" : ""
                        }`}
                    />
                </Link>

                {/* Right: nav links + reserve CTA */}
                <div className="hidden lg:flex items-center gap-6 flex-1 justify-end">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `nav-link ${
                                    transparent
                                        ? "text-white/85 hover:text-white"
                                        : "text-aegean-800 hover:text-aegean-500"
                                } ${isActive ? "!text-aegean-400" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <button
                        data-testid="nav-reserve-btn"
                        onClick={openReserve}
                        className={`btn-pill text-xs ${
                            transparent
                                ? "bg-aegean-400 text-white hover:bg-aegean-500"
                                : "bg-aegean-500 text-white hover:bg-aegean-600"
                        }`}
                    >
                        {t("nav.reserveTable")}
                    </button>
                </div>

                {/* Mobile menu button */}
                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className={`lg:hidden p-2 rounded-full ${
                        transparent ? "text-white" : "text-aegean-800"
                    }`}
                    aria-label="Menu"
                >
                    {open ? <X size={24} /> : <MenuIcon size={24} />}
                </button>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-aegean-100"
                >
                    <div className="container-x py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `nav-link text-base ${
                                        isActive ? "text-aegean-400" : "text-aegean-800"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <button
                            data-testid="mobile-nav-reserve-btn"
                            onClick={() => { setOpen(false); openReserve(); }}
                            className="btn-primary mt-2 w-full"
                        >
                            {t("nav.reserveTable")}
                        </button>
                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="mobile-phone-link"
                            className="flex items-center gap-2 nav-link text-aegean-700"
                        >
                            <Phone size={16} /> {PHONE}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
